import { supabase } from '../utils/supabase';
import { latePenaltyNotice } from '../utils/sessionPresence';

export const PRESENCE_RPC_CODES = {
  SIGNED_IN: 'SIGNED_IN',
  PAUSED: 'PAUSED',
  RESUMED: 'RESUMED',
  ALREADY_READY: 'ALREADY_READY',
  ALREADY_PAUSED: 'ALREADY_PAUSED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  GUEST_NOT_ALLOWED: 'GUEST_NOT_ALLOWED',
  NOT_IN_SESSION: 'NOT_IN_SESSION',
  SESSION_NOT_LIVE: 'SESSION_NOT_LIVE',
  SESSION_TYPE_NOT_ALLOWED: 'SESSION_TYPE_NOT_ALLOWED',
  INVALID_ACTION: 'INVALID_ACTION',
  INVALID_TRANSITION: 'INVALID_TRANSITION',
  ON_COURT: 'ON_COURT',
  IN_UPCOMING: 'IN_UPCOMING',
  SCHEDULED_GAME: 'SCHEDULED_GAME',
  MISSING_SESSION: 'MISSING_SESSION',
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
};

const ERROR_MESSAGES = {
  [PRESENCE_RPC_CODES.UNAUTHORIZED]: 'You need to be signed in.',
  [PRESENCE_RPC_CODES.GUEST_NOT_ALLOWED]: 'The host needs to sign you in for this session.',
  [PRESENCE_RPC_CODES.NOT_IN_SESSION]: 'You are not part of this session.',
  [PRESENCE_RPC_CODES.SESSION_NOT_LIVE]: 'You can only change this once the session is live.',
  [PRESENCE_RPC_CODES.SESSION_TYPE_NOT_ALLOWED]: 'Self sign-in is only available in competitive sessions.',
  [PRESENCE_RPC_CODES.INVALID_ACTION]: 'Unknown presence action.',
  [PRESENCE_RPC_CODES.INVALID_TRANSITION]: 'Ask the host to change your session status.',
  [PRESENCE_RPC_CODES.ON_COURT]:
    'You can pause after this game finishes. Or contact the host of the session to remove you from the game.',
  [PRESENCE_RPC_CODES.IN_UPCOMING]:
    'You can pause after this upcoming game. Or contact the host of the session to remove you from the upcoming game.',
  [PRESENCE_RPC_CODES.SCHEDULED_GAME]: 'Ask the host to change your scheduled game before you pause.',
  [PRESENCE_RPC_CODES.MISSING_SESSION]: 'Session is required.',
  [PRESENCE_RPC_CODES.SESSION_NOT_FOUND]: 'Session not found.',
};

const SUCCESS_MESSAGES = {
  [PRESENCE_RPC_CODES.SIGNED_IN]: "You're in the queue and ready to play.",
  [PRESENCE_RPC_CODES.PAUSED]: "You're paused. Resume when you're ready to play again.",
  [PRESENCE_RPC_CODES.RESUMED]: "You're back in the queue.",
  [PRESENCE_RPC_CODES.ALREADY_READY]: "You're already in the queue.",
  [PRESENCE_RPC_CODES.ALREADY_PAUSED]: "You're already paused.",
};

export function parsePresenceRpcResult(rpcData) {
  if (!rpcData || typeof rpcData !== 'object') {
    return { success: false, code: 'UNKNOWN', error: 'Could not update your status. Try again.' };
  }

  const code = rpcData.code || 'UNKNOWN';
  const lateJoinerPenalty = Number(rpcData.late_joiner_penalty) || 0;

  if (rpcData.ok === true) {
    const penaltyMessage =
      code === PRESENCE_RPC_CODES.SIGNED_IN || code === PRESENCE_RPC_CODES.RESUMED
        ? latePenaltyNotice(lateJoinerPenalty)
        : null;
    return {
      success: true,
      code,
      status: rpcData.status || null,
      lateJoinerPenalty,
      penaltyApplied: Boolean(rpcData.penalty_applied),
      waitlistPosition: rpcData.waitlist_position ?? null,
      message: penaltyMessage || SUCCESS_MESSAGES[code] || "You're updated.",
      penaltyMessage,
    };
  }

  return {
    success: false,
    code,
    error: rpcData.message || ERROR_MESSAGES[code] || 'Could not update your status. Try again.',
  };
}

export async function setOwnSessionPresence(sessionId, action) {
  if (!sessionId) {
    return parsePresenceRpcResult({ ok: false, code: PRESENCE_RPC_CODES.MISSING_SESSION });
  }

  const { data, error } = await supabase.rpc('set_own_session_presence', {
    p_session_id: sessionId,
    p_action: action,
  });

  if (error) {
    console.error('[sessionPresence] set_own_session_presence failed:', error);
    return {
      success: false,
      code: 'RPC_ERROR',
      error: error.message || 'Could not update your status. Try again.',
    };
  }

  return parsePresenceRpcResult(data);
}
